import type {
  FrontendApi,
  RegistrationFlow,
  SuccessfulNativeRegistration,
  UiNode,
  UiNodeInputAttributes,
} from '@ory/client-fetch';

import { handleOryCall, logger } from '@/utils';

interface SendRegistrationOtpResult {
  success: boolean;
  flowId?: string;
  csrfToken?: string;
  error?: string;
}

/**
 * Sends an OTP for user registration
 * @param ory The Ory client instance
 * @param phoneNumber The user's phone number
 * @returns Result object with success status and flow data or error
 */
const sendRegistrationOtp = async (
  ory: FrontendApi,
  phoneNumber: string,
): Promise<SendRegistrationOtpResult> => {
  logger.debug(`Sending registration OTP for: ${phoneNumber}`);

  try {
    // Create registration flow using our utility to handle any status code
    const flowResult = await handleOryCall<RegistrationFlow>(
      () => ory.createBrowserRegistrationFlow(),
      'Registration flow creation',
    );

    // Check if we got a usable flow data
    if (flowResult.error || !flowResult.data || !flowResult.data.id) {
      throw new Error(
        'Failed to create registration flow: ' +
          (flowResult.error?.message || 'No flow data returned'),
      );
    }

    const flowData = flowResult.data;

    // Extract CSRF token
    const csrfTokenNode = flowData.ui.nodes.find(
      (node: UiNode) =>
        node.attributes.node_type === 'input' &&
        (node.attributes as UiNodeInputAttributes).name === 'csrf_token',
    );
    const csrfToken = csrfTokenNode?.attributes
      ? ((csrfTokenNode.attributes as UiNodeInputAttributes).value as string)
      : undefined;

    if (!csrfToken) {
      throw new Error('CSRF token not found in flow');
    }

    // Update registration flow to send OTP using our utility to handle any status code
    await handleOryCall<SuccessfulNativeRegistration>(
      () =>
        ory.updateRegistrationFlow({
          flow: flowData.id,
          updateRegistrationFlowBody: {
            method: 'code',
            traits: {
              phone_number: phoneNumber,
            },
            csrf_token: csrfToken,
          },
        }),
      'Registration OTP sending',
    );

    return {
      success: true,
      flowId: flowData.id,
      csrfToken,
    };
  } catch (error) {
    logger.error(
      `Registration OTP error: ${error instanceof Error ? error.message : JSON.stringify(error, undefined, 2)}`,
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

export { sendRegistrationOtp };
export type { SendRegistrationOtpResult };
